import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Heart, ShieldCheck, UploadCloud, Loader2, Sparkles, ArrowLeft, AlertTriangle,
} from "lucide-react";
import { celebrate } from "@/lib/confetti";
import NonprofitBadge from "@/components/NonprofitBadge";
import NonprofitOnboardingWizard from "@/components/NonprofitOnboardingWizard";

/**
 * NonprofitVerification
 * ---------------------
 * Two-path verification UI:
 *
 *   1) EIN auto-verify (fastest, ~5 s round-trip via ProPublica). If the org
 *      is a registered 501(c)(3) we flip the user to `verified` instantly.
 *   2) Document upload (fallback). Any non-PDF/JPG file the browser will
 *      base64 is accepted — the admin queue handles review.
 *
 * Both paths converge on the same success card: verified badge + 50% Pro
 * pricing unlocked + priority discovery placement.
 */
export default function NonprofitVerification() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [ein, setEin] = useState("");
  const [orgName, setOrgName] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDocPath, setShowDocPath] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/nonprofit/status");
      setStatus(data);
      if (data.nonprofit_ein) setEin(data.nonprofit_ein);
      if (data.nonprofit_org_name) setOrgName(data.nonprofit_org_name);
      if (data.nonprofit_status === "pending_review" ||
          data.nonprofit_status === "rejected") {
        setShowDocPath(true);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load nonprofit status");
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const verifyEin = async () => {
    if (!ein.trim()) { toast.error("Enter your 9-digit EIN."); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/nonprofit/verify-ein", {
        ein: ein.trim(),
        org_name: orgName.trim() || undefined,
      });
      if (data.status === "verified") {
        celebrate({ particleCount: 140, scalar: 1.15 });
        toast.success(data.message || "Nonprofit verified!");
      } else {
        toast.info(data.message || "We couldn't auto-verify — please upload your documentation.");
        setShowDocPath(true);
      }
      await refresh?.();
      await loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
    } finally { setVerifying(false); }
  };

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("File too large — max 6 MB."); return; }
    setFile(f);
  };

  const uploadDocument = async () => {
    if (!file) { toast.error("Pick a file first."); return; }
    if (!orgName.trim()) { toast.error("Enter your organization's legal name."); return; }
    setUploading(true);
    try {
      // Base64-encode via FileReader — data URL is fine, backend stores as-is.
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post("/nonprofit/upload-document", {
        document_base64: dataUrl,
        filename: file.name,
        org_name: orgName.trim(),
        ein: ein.trim() || undefined,
      });
      toast.success("Uploaded — your document is in the admin queue.");
      setFile(null);
      await refresh?.();
      await loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!status) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: "var(--avt-purple)" }} />
      </div>
    );
  }

  const isVerified = status.nonprofit_status === "verified";
  const isPending = status.nonprofit_status === "pending_review";
  const isRejected = status.nonprofit_status === "rejected";

  return (
    <div className="mx-auto max-w-2xl px-6 lg:px-10 py-12" data-testid="nonprofit-verification-page">
      <Link
        to={user?.role === "organizer" ? "/organizer/dashboard" : "/vendor/dashboard"}
        className="inline-flex items-center gap-1 text-sm mb-6"
        style={{ color: "var(--avt-purple)" }}
      >
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <div
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
        style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A", border: "1.5px solid #84CC16" }}
      >
        <Heart className="h-3 w-3" fill="#84CC16" strokeWidth={0} /> Nonprofit Verification
      </div>
      <h1 className="font-serif text-4xl lg:text-5xl mt-3 leading-tight">
        {isVerified ? (
          <><span style={{ color: "var(--avt-lime)" }}>You&rsquo;re verified.</span> Thanks for the good work.</>
        ) : (
          <><span style={{ color: "var(--avt-purple)" }}>Prove your 501(c)(3)</span> — unlock 50% off Pro forever.</>
        )}
      </h1>
      <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        Verified nonprofits get <strong>50% off Maker Pro / Organizer Pro forever</strong>, a{" "}
        <NonprofitBadge user={{ nonprofit_status: "verified" }} size="sm" /> on every card + profile, and
        <strong> priority placement</strong> in event and vendor discovery.
      </p>

      {/* First-run 3-step wizard (renders nothing for verified/pending/rejected
          users or when already dismissed). "Start verifying" scrolls the EIN
          form into view so freshly signed-up nonprofits know exactly where
          to look next. */}
      <NonprofitOnboardingWizard
        user={user}
        status={status}
        onStart={() => {
          setTimeout(() => {
            const el = document.querySelector("[data-testid=nonprofit-ein-card]");
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
            document.querySelector("[data-testid=nonprofit-ein-input]")?.focus();
          }, 60);
        }}
      />

      {isVerified && (
        <div
          data-testid="nonprofit-verified-card"
          className="mt-8 rounded-xl p-6 sm:p-8"
          style={{
            background: "linear-gradient(135deg, rgba(132,204,22,0.10), rgba(44,196,217,0.10))",
            border: "2px solid #84CC16",
          }}
        >
          <ShieldCheck className="h-10 w-10" style={{ color: "#4B7A0A" }} />
          <h2 className="font-serif text-2xl mt-3">Verified as {status.nonprofit_org_name}</h2>
          <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
            EIN {status.nonprofit_ein} · verified{" "}
            {status.nonprofit_verified_at
              ? new Date(status.nonprofit_verified_at).toLocaleDateString()
              : "recently"}
            {" · via "}
            {status.nonprofit_verification_method === "ein_auto" ? "IRS Publication 78 (ProPublica)" : "admin document review"}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/pricing" className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> See your 50%-off Pro pricing
            </Link>
          </div>
        </div>
      )}

      {isPending && (
        <div
          data-testid="nonprofit-pending-card"
          className="mt-8 rounded-xl p-5"
          style={{
            backgroundColor: "rgba(249,115,22,0.08)",
            border: "1.5px solid var(--avt-orange)",
          }}
        >
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest"
               style={{ color: "var(--avt-orange)" }}>
            <Loader2 className="h-3 w-3 animate-spin" /> Awaiting admin review
          </div>
          <p className="mt-2 text-sm">
            Thanks — we&rsquo;ve got your submission. Reviews usually complete within one business day.
            You&rsquo;ll receive an email as soon as it&rsquo;s approved.
          </p>
        </div>
      )}

      {isRejected && (
        <div
          data-testid="nonprofit-rejected-card"
          className="mt-8 rounded-xl p-5"
          style={{
            backgroundColor: "rgba(236,72,153,0.08)",
            border: "1.5px solid var(--avt-pink)",
          }}
        >
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest"
               style={{ color: "var(--avt-pink)" }}>
            <AlertTriangle className="h-3 w-3" /> Previous submission not approved
          </div>
          <p className="mt-2 text-sm">
            {status.nonprofit_rejection_reason ||
              "Please re-submit with updated documentation."}
          </p>
          <p className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>
            You can submit again at any time — the flow below picks up where you left off.
          </p>
        </div>
      )}

      {!isVerified && (
        <>
          {/* Path 1: EIN auto-verify */}
          <div className="mt-8 card-warm rounded-xl p-6" data-testid="nonprofit-ein-card">
            <div className="overline" style={{ color: "var(--avt-purple)" }}>Fastest path · ~5 seconds</div>
            <h2 className="font-serif text-2xl mt-2">Verify by EIN</h2>
            <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
              We&rsquo;ll check the IRS Publication 78 list (via ProPublica&rsquo;s public API).
              If your org is a registered 501(c)(3), we&rsquo;ll approve you instantly.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="np-ein" className="text-xs">EIN (9 digits)</Label>
                <Input
                  id="np-ein"
                  data-testid="nonprofit-ein-input"
                  placeholder="12-3456789"
                  value={ein}
                  onChange={(e) => setEin(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="np-org" className="text-xs">Legal org name <span style={{ color: "var(--ink-soft)" }}>(optional)</span></Label>
                <Input
                  id="np-org"
                  data-testid="nonprofit-org-input"
                  placeholder="Neighborhood Rescue Barn"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <Button
              onClick={verifyEin}
              disabled={verifying || !ein.trim()}
              data-testid="nonprofit-verify-btn"
              className="btn-primary mt-4 px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60"
            >
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {verifying ? "Checking IRS…" : "Verify with IRS"}
            </Button>
          </div>

          {/* Path 2: Document upload */}
          <div className="mt-6 card-warm rounded-xl p-6" data-testid="nonprofit-doc-card">
            <div className="overline" style={{ color: "var(--avt-teal)" }}>Fallback · admin review</div>
            <h2 className="font-serif text-2xl mt-2">Upload your IRS letter</h2>
            <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
              New filers can take 6-9 months to appear in the IRS public list.
              Upload your IRS determination letter (or comparable proof) — an admin will review within one business day.
            </p>
            {!showDocPath ? (
              <button
                type="button"
                onClick={() => setShowDocPath(true)}
                data-testid="nonprofit-show-doc-btn"
                className="mt-4 text-sm underline"
                style={{ color: "var(--avt-teal)" }}
              >
                Show upload option
              </button>
            ) : (
              <div className="mt-4 grid gap-3">
                <div>
                  <Label htmlFor="np-org2" className="text-xs">Legal org name</Label>
                  <Input
                    id="np-org2"
                    data-testid="nonprofit-org-input-doc"
                    placeholder="Neighborhood Rescue Barn"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <label
                  data-testid="nonprofit-file-label"
                  className="cursor-pointer flex flex-col items-center justify-center rounded-lg p-6 border-2 border-dashed"
                  style={{ borderColor: file ? "var(--avt-teal)" : "rgba(0,0,0,0.15)" }}
                >
                  <UploadCloud className="h-7 w-7 mb-1" style={{ color: "var(--avt-teal)" }} />
                  <div className="text-sm">
                    {file ? file.name : "Tap to upload IRS letter (PDF or image)"}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>up to 6 MB</div>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    data-testid="nonprofit-file-input"
                    onChange={(e) => onFile(e.target.files?.[0])}
                  />
                </label>
                <Button
                  onClick={uploadDocument}
                  disabled={uploading || !file || !orgName.trim()}
                  data-testid="nonprofit-upload-btn"
                  className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  {uploading ? "Uploading…" : "Submit for review"}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
